import { useState, useEffect, useCallback } from 'react';
import { ecosystemAPI } from '../../services/api';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import ImagePickerField from '../../components/ui/ImagePickerField';

const PILLARS = [
  { value:'education_training',  label:'Education & Training',  color:'var(--blue)'   },
  { value:'services_consulting', label:'Services & Consulting', color:'var(--accent)' },
  { value:'products_platforms',  label:'Products & Platforms',  color:'var(--green)'  },
  { value:'media_communication', label:'Media & Communication', color:'var(--amber)'  },
  { value:'innovation_research', label:'Innovation & Research', color:'var(--blue)'   },
];
const BRAND_CLASSES = ['core','sub_brand','program','product_platform'];
const ENTITY_TYPES  = ['brand','platform','program','product'];

export default function EcosystemPage() {
  const [entities, setEntities] = useState([]);
  const [pagination, setPag]    = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [filter, setFilter]     = useState({ pillar:'', status:'' });
  const [modal, setModal]       = useState(false);
  const [selected, setSel]      = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({
    name:'', slug:'', type:'brand', pillar:'services_consulting',
    brand_class:'sub_brand', description:'', purpose:'',
    target_audience:'', relationship_note:'', status:'active',
    media_url:'', logo_url:'',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Strip empty strings — pillar:'' would filter out everything
      const params = { page };
      if (filter.pillar) params.pillar = filter.pillar;
      if (filter.status) params.status = filter.status;
      const res = await ecosystemAPI.list(params);
      setEntities(res.data.data || []); setPag(res.data.pagination);
    } catch { setEntities([]); } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name:'', slug:'', type:'brand', pillar:'services_consulting', brand_class:'sub_brand', description:'', purpose:'', target_audience:'', relationship_note:'', status:'active', media_url:'', logo_url:'' });
    setSel(null); setModal(true);
  };
  const openEdit = (e) => {
    setForm({ name:e.name, slug:e.slug, type:e.type, pillar:e.pillar, brand_class:e.brand_class, description:e.description||'', purpose:e.purpose||'', target_audience:e.target_audience||'', relationship_note:e.relationship_note||'', status:e.status, media_url:e.media_url||'', logo_url:e.logo_url||'' });
    setSel(e); setModal(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      if (!selected) await ecosystemAPI.create(form);
      else           await ecosystemAPI.update(selected.id, form);
      setModal(false); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const pillarColor = (p) => PILLARS.find(x=>x.value===p)?.color || 'var(--text-muted)';
  const pillarLabel = (p) => PILLARS.find(x=>x.value===p)?.label || p;

  const COLS = [
    { key:'media_url', label:'', render:(v,row) => (
      <div style={{ width:36, height:36, borderRadius:6, overflow:'hidden', background:'var(--bg-elevated)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
        {v||row.logo_url
          ? <img src={v||row.logo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          : <div style={{ width:8, height:8, borderRadius:'50%', background:pillarColor(row.pillar) }}/>}
      </div>
    )},
    { key:'name', label:'Entity', render:(v,row) => (
      <div>
        <div style={{ fontWeight:600 }}>{v}</div>
        <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'monospace' }}>{row.slug}</div>
      </div>
    )},
    { key:'pillar',      label:'Pillar', render:v => <span style={{ fontSize:12, color:pillarColor(v) }}>{pillarLabel(v)}</span> },
    { key:'brand_class', label:'Class',  render:v => <span style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'capitalize' }}>{v?.replace('_',' ')}</span> },
    { key:'type',        label:'Type',   render:v => <span style={{ fontSize:12, color:'var(--text-muted)', textTransform:'capitalize' }}>{v}</span> },
    { key:'status',      label:'Status', render:v => <Badge status={v}/> },
  ];

  const pillBtn = (val) => ({
    padding:'6px 14px', borderRadius:20, fontSize:12, cursor:'pointer',
    background: filter.pillar===val ? (val?pillarColor(val)+'22':'var(--accent)') : 'var(--bg-elevated)',
    color:      filter.pillar===val ? (val?pillarColor(val):'#0d0f12')            : 'var(--text-secondary)',
    border:     `1px solid ${filter.pillar===val ? (val?pillarColor(val):'var(--accent)') : 'var(--border)'}`,
    fontWeight: filter.pillar===val ? 600 : 400,
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>

      {/* Pillar filter buttons */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <button style={pillBtn('')} onClick={()=>setFilter(f=>({...f,pillar:''}))}>All Pillars</button>
        {PILLARS.map(p=>(
          <button key={p.value} style={pillBtn(p.value)}
            onClick={()=>setFilter(f=>({...f,pillar:f.pillar===p.value?'':p.value}))}>
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <Select value={filter.status} onChange={e=>setFilter(f=>({...f,status:e.target.value}))} style={{ width:160 }}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="coming_soon">Coming Soon</option>
          </Select>
          <span style={{ fontSize:12, color:'var(--text-muted)' }}>{pagination?.total??0} entities</span>
        </div>
        <Button onClick={openCreate}>+ New Entity</Button>
      </div>

      <div style={{ background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
        <Table columns={COLS} data={entities} loading={loading} onRowClick={openEdit}/>
        <Pagination pagination={pagination} onPage={setPage}/>
      </div>

      <Modal open={modal} onClose={()=>setModal(false)} title={selected?`Edit — ${selected.name}`:'New Ecosystem Entity'} width={680}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <ImagePickerField label="Cover Image" value={form.media_url} onChange={url=>setForm(f=>({...f,media_url:url}))}/>
          <ImagePickerField label="Logo / Icon"  value={form.logo_url}  onChange={url=>setForm(f=>({...f,logo_url:url}))}/>
          <div style={{ gridColumn:'1 / -1' }}>
            <Input label="Entity Name" value={form.name} onChange={e=>{
              const slug = e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
              setForm(f=>({...f,name:e.target.value,slug}));
            }}/>
          </div>
          <Input label="Slug" value={form.slug} onChange={e=>setForm(f=>({...f,slug:e.target.value}))} style={{ fontFamily:'monospace' }}/>
          <Select label="Type" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
            {ENTITY_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
          </Select>
          <Select label="Pillar" value={form.pillar} onChange={e=>setForm(f=>({...f,pillar:e.target.value}))}>
            {PILLARS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
          <Select label="Brand Class" value={form.brand_class} onChange={e=>setForm(f=>({...f,brand_class:e.target.value}))}>
            {BRAND_CLASSES.map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}
          </Select>
          <Select label="Status" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="coming_soon">Coming Soon</option>
          </Select>
          <div style={{ gridColumn:'1 / -1' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={3} style={taStyle}/>
          </div>
          <Input label="Purpose"          value={form.purpose}           onChange={e=>setForm(f=>({...f,purpose:e.target.value}))}/>
          <Input label="Target Audience"  value={form.target_audience}   onChange={e=>setForm(f=>({...f,target_audience:e.target.value}))}/>
          <div style={{ gridColumn:'1 / -1' }}>
            <Input label="Relationship Note" value={form.relationship_note} onChange={e=>setForm(f=>({...f,relationship_note:e.target.value}))}/>
          </div>
          <div style={{ gridColumn:'1 / -1', display:'flex', gap:10, justifyContent:'flex-end', paddingTop:6 }}>
            <Button variant="secondary" onClick={()=>setModal(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{selected?'Save Changes':'Create Entity'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize:12, color:'var(--text-secondary)', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.03em', display:'block', marginBottom:6 };
const taStyle    = { width:'100%', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', color:'var(--text-primary)', padding:'9px 12px', fontSize:13, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' };